import { useState } from "react";
import {
  Box,
  Dialog,
  DialogContent,
  Stack,
  Typography,
  alpha,
} from "@mui/material";
import { AlternateEmail, QrCode2, Share, WhatsApp } from "@mui/icons-material";
import QRCode from "react-qr-code";
import { showSuccessToast } from "../../../../../../../../../components/common/ShowToast";

interface ShareRailProps {
  websiteStreamLink?: string;
  title?: string;
  accentColor?: string;
}

const ShareRail = ({
  websiteStreamLink,
  title,
  accentColor = "#424242",
}: ShareRailProps) => {
  const [openQr, setOpenQr] = useState(false);

  const handleWhatsApp = () => {
    if (!websiteStreamLink) return;
    const text = encodeURIComponent(
      `It is with great sadness that we share the livestream link for the funeral ${title || ""}.\n\nYou can view it here: ${websiteStreamLink}`,
    );
    window.open(`https://wa.me/?text=${text}`, "_blank");
  };

  const handleEmail = () => {
    if (!websiteStreamLink) return;
    const subject = encodeURIComponent(
      `Funeral Service for ${title || ""} - Livestream Link`,
    );
    const body = encodeURIComponent(
      `Dear friends and family,\n\nWe are sharing the livestream link for the funeral service of ${title || ""}\n\nYou can view the livestream here: ${websiteStreamLink}\n\nSincerely,`,
    );
    window.open(`mailto:?subject=${subject}&body=${body}`, "_blank");
  };

  const handleMore = async () => {
    if (!websiteStreamLink) return;
    if (navigator.share) {
      try {
        await navigator.share({ title, url: websiteStreamLink });
        return;
      } catch {
        // user cancelled or share failed — fall back to copy
      }
    }
    try {
      await navigator.clipboard.writeText(websiteStreamLink);
      showSuccessToast("Link Copied Successfully");
    } catch (err) {
      console.error("Failed to copy URL: ", err);
    }
  };

  const options = [
    {
      icon: QrCode2,
      label: "QR Code",
      onClick: () => setOpenQr(true),
      color: accentColor,
    },
    {
      icon: WhatsApp,
      label: "Whatsapp",
      onClick: handleWhatsApp,
      color: "#25D366",
    },
    {
      icon: AlternateEmail,
      label: "E-mail",
      onClick: handleEmail,
      color: "#EA4335",
    },
    { icon: Share, label: "More", onClick: handleMore, color: accentColor },
  ];

  return (
    <>
      <Stack spacing={1.5} alignItems="center" sx={{ pt: 1 }}>
        <Typography sx={{ fontSize: "11px", fontWeight: 600 }}>
          Share:
        </Typography>
        {options.map(({ icon: Icon, label, onClick, color }) => (
          <Stack
            key={label}
            alignItems="center"
            spacing={0.3}
            onClick={onClick}
            sx={{ cursor: "pointer" }}
          >
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: "50%",
                bgcolor: alpha(color, 0.12),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                "&:hover": { bgcolor: alpha(color, 0.22) },
              }}
            >
              <Icon sx={{ fontSize: 18, color }} />
            </Box>
            <Typography sx={{ fontSize: "10px", color: "text.secondary" }}>
              {label}
            </Typography>
          </Stack>
        ))}
      </Stack>

      <Dialog open={openQr} onClose={() => setOpenQr(false)}>
        <DialogContent
          sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}
        >
          <QRCode value={websiteStreamLink || ""} size={200} />
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ShareRail;
